<script setup lang="ts">
// ⚙️ Global menu (v2.32, per Javi): the top-right chip that used to be
// just the music mute now opens a small menu — music, sound effects,
// the Collection, and a hop home. Rides on EVERY screen via app.vue.
// ⚡ v4.89: this menu rides EVERY screen — it must not pull the 250KB
// changelog array into the entry chunk for one version string.
import { CURRENT_VERSION } from '~/data/version'
import { COACH, coachOn, coachSeen, coachTips, markCoach, unmarkCoach } from '~/utils/firstRunGuide'

const music = useMusic()
const sfx = useSfx()
const haptics = useHaptics()
function onHaptics() {
  haptics.setEnabled(!haptics.enabled.value)
  sfx.tap()
}
// ⏩ v7.42 (per Javi): "Enable Tap to Fast Forward" lives here now, not in the banner.
const tapFf = useTapFastForward()
function onTapFf() {
  sfx.tap()
  tapFf.setEnabled(!tapFf.enabled.value)
}
// 🎭 v7.56 (per Javi): the face-off scenes before acts and bosses, on by default.
const storyScenes = useStoryScenes()
function onStoryScenes() {
  sfx.tap()
  storyScenes.setEnabled(!storyScenes.enabled.value)
}
const route = useRoute()
const open = ref(false)
const emit = defineEmits<{ 'open-change': [open: boolean] }>()
watch(open, value => emit('open-change', value))
onUnmounted(() => emit('open-change', false))
const dismiss = useBackdropDismiss(() => { open.value = false })
// 📖 v7.04: the guide, reachable from every screen.
const guideOpen = ref(false)
// 🎓 v7.40: the guide can start the practice - the menu under it closes too.
function onGuidePractice() {
  guideOpen.value = false
  open.value = false
}

const version = CURRENT_VERSION

// ⚠️ v5.62 (per Javi): Abandon Run lives in this menu too — with a
// warning pop-up, never a one-tap loss. Hidden on the title (no run on
// screen there) and after a victory (nothing left to abandon).
const { run, abandonRun } = useRun()
const confirmAbandon = ref(false)
const canAbandon = computed(() =>
  route.path !== '/' && !!run.value && !run.value.bossDefeated)
watch(open, (o) => { if (!o) confirmAbandon.value = false })
function onAbandonYes() {
  sfx.confirm()
  confirmAbandon.value = false
  open.value = false
  abandonRun()
  navigateTo('/')
}

// 🎓 v7.41 (per Javi, §3 C): on a FIRST RUN, "Stop guidance" ends the
// step-by-step coach only (the one-liners stay), and offers an undo - never
// the WIP's "Turn off guidance" beside every coach line, which wiped every
// future tip with no way back. Small on purpose: it is not "close".
const guideStoppable = computed(() => route.path !== '/' && coachOn(run.value))
const guideStopped = computed(() => route.path !== '/' && coachTips(run.value) && coachSeen(run.value, COACH.off))
function onStopGuidance() {
  sfx.deselect()
  markCoach(run.value, COACH.off)
}
function onUndoGuidance() {
  sfx.tap()
  unmarkCoach(run.value, COACH.off)
}

function onToggle() {
  sfx.tap()
  open.value = !open.value
}
// 📱 M6 (useBackStack): the screens' back opens this menu (the map, a paused
// node), and back over the abandon warning keeps the run before the menu goes.
useBackMenu(() => { if (!open.value) onToggle() })
useBackLayer('menu-abandon-confirm', () => confirmAbandon.value, () => { sfx.deselect(); confirmAbandon.value = false })
function onCollection() {
  sfx.tap()
  open.value = false
  navigateTo('/collection')
}
function onHome() {
  sfx.tap()
  open.value = false
  navigateTo('/')
}
const { t, locale } = useLocale()
const hapticsLabel = computed(() => ({ en: 'Haptics', es: 'Vibración', fr: 'Vibrations' })[locale.value])
</script>

<template>
  <button
    class="menu-chip"
    :title="t('common.menu')"
    :aria-label="t('common.menu')"
    data-testid="menu-toggle"
    @click="onToggle"
  >
    ☰
  </button>
  <!-- 🪄 Teleported (v2.52, per Javi: the menu "doesn't work too well" in
       a node): inside the encounter's sticky header the fixed backdrop
       was trapped in that header's stacking context. -->
  <HowToPlay v-if="guideOpen" @close="guideOpen = false" @practice="onGuidePractice" />
  <Teleport to="body">
  <div
    v-if="open"
    class="menu-backdrop"
    data-testid="game-menu"
    @pointerdown="dismiss.down"
    @pointerup="dismiss.up"
  >
    <div class="card game-menu-card">
      <ModalClose @close="open = false"/>
      <h2 class="game-menu-title">{{ t('common.menu') }}</h2>
      <div class="game-menu-switches">
        <div class="game-menu-volume" :class="{ 'is-muted': music.muted.value }">
          <button class="menu-audio-toggle" :aria-pressed="music.muted.value" :aria-label="t(music.muted.value ? 'gamemenu.unmuteMusic' : 'gamemenu.muteMusic')" data-testid="menu-music" @click="sfx.tap(); music.toggleMute()">
            <img :src="music.muted.value ? '/ui/icon-mute.webp' : '/ui/icon-unmute.webp'" alt="" class="menu-switch-icon" draggable="false">
          </button>
          <div class="menu-volume-control">
            <label for="menu-music-volume">{{ t('gamemenu.music', { state: `${Math.round(music.volume.value * 100)}%` }) }}<span v-if="music.muted.value"> · {{ t('common.off') }}</span></label>
            <input id="menu-music-volume" data-testid="menu-music-volume" type="range" min="0" max="100" step="1" :value="Math.round(music.volume.value * 100)" :aria-label="t('gamemenu.musicVolume')" :aria-valuetext="`${Math.round(music.volume.value * 100)}%`" @input="music.setVolume(Number(($event.target as HTMLInputElement).value) / 100)">
          </div>
        </div>
        <div class="game-menu-volume" :class="{ 'is-muted': sfx.muted.value }">
          <button class="menu-audio-toggle" :aria-pressed="sfx.muted.value" :aria-label="t(sfx.muted.value ? 'gamemenu.unmuteSfx' : 'gamemenu.muteSfx')" data-testid="menu-sfx" @click="sfx.toggleMute(); sfx.tap()">
            <img :src="sfx.muted.value ? '/ui/icon-mute.webp' : '/ui/icon-unmute.webp'" alt="" class="menu-switch-icon" draggable="false">
          </button>
          <div class="menu-volume-control">
            <label for="menu-sfx-volume">{{ t('gamemenu.sfx', { state: `${Math.round(sfx.volume.value * 100)}%` }) }}<span v-if="sfx.muted.value"> · {{ t('common.off') }}</span></label>
            <input id="menu-sfx-volume" data-testid="menu-sfx-volume" type="range" min="0" max="100" step="1" :value="Math.round(sfx.volume.value * 100)" :aria-label="t('gamemenu.sfxVolume')" :aria-valuetext="`${Math.round(sfx.volume.value * 100)}%`" @input="sfx.setVolume(Number(($event.target as HTMLInputElement).value) / 100)" @change="sfx.tap()">
          </div>
        </div>
        <button class="game-menu-switch menu-wide-switch" :aria-pressed="tapFf.enabled.value" data-testid="menu-tap-ff" @click="onTapFf">
          <img src="/ui/icon-fastforward.webp" alt="" class="menu-switch-icon" :class="{ 'is-off': !tapFf.enabled.value }" draggable="false">
          <span>{{ t('gamemenu.tapFf', { state: tapFf.enabled.value ? t('common.on') : t('common.off') }) }}</span>
        </button>
        <button class="game-menu-switch menu-paired-switch" :aria-pressed="haptics.enabled.value" data-testid="menu-haptics" @click="onHaptics">
          <svg viewBox="0 0 24 24" class="menu-switch-icon" :class="{ 'is-off': !haptics.enabled.value }" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <rect x="8" y="4" width="8" height="16" rx="2" />
            <path d="M4 7 2 10l2 4-2 3M20 7l2 3-2 4 2 3M11 17h2" />
          </svg>
          <span>{{ hapticsLabel }}: {{ t(haptics.enabled.value ? 'common.on' : 'common.off') }}</span>
        </button>
        <button class="game-menu-switch menu-paired-switch" :aria-pressed="storyScenes.enabled.value" data-testid="menu-story-scenes" @click="onStoryScenes">
          <img src="/ui/icon-boss.webp" alt="" class="menu-switch-icon" :class="{ 'is-off': !storyScenes.enabled.value }" draggable="false">
          <span>{{ t('gamemenu.storyScenes', { state: storyScenes.enabled.value ? t('common.on') : t('common.off') }) }}</span>
        </button>
      </div>
      <button class="game-menu-row" data-testid="menu-collection" @click="onCollection">
        <img src="/ui/icon-collection.webp" alt="" class="menu-row-icon" draggable="false">
        {{ t('menu.collection') }}
      </button>
      <button class="game-menu-row" data-testid="menu-guide" @click="sfx.tap(); guideOpen = true">
        <img src="/ui/icon-scouting.webp" alt="" class="menu-row-icon" draggable="false">
        {{ t('title.howToPlay') }}
      </button>
      <button v-if="route.path !== '/'" class="game-menu-row" data-testid="menu-home" @click="onHome">
        <img src="/logo.webp?v=4" alt="" class="menu-row-icon" draggable="false">
        {{ t('gamemenu.titleScreen') }}
      </button>
      <button v-if="guideStoppable" class="game-menu-guidance" data-testid="menu-stop-guidance" @click="onStopGuidance">
        {{ t('gamemenu.stopGuidance') }}
      </button>
      <p v-else-if="guideStopped" class="game-menu-guidance-off" data-testid="menu-guidance-off" role="status">
        {{ t('gamemenu.guidanceOff') }}
        <button class="game-menu-guidance" data-testid="menu-guidance-undo" @click="onUndoGuidance">{{ t('gamemenu.undo') }}</button>
      </p>
      <button v-if="canAbandon" class="btn btn-danger game-menu-danger" data-testid="menu-abandon" @click="sfx.tap(); confirmAbandon = true">
        {{ t('menu.abandonRun') }}
      </button>
      <p class="game-menu-note">{{ version }}</p>
    </div>
    <!-- ⚠️ The warning pop-up rides over the menu; tapping outside it
         keeps the run (never an accidental abandon). -->
    <div v-if="confirmAbandon" class="menu-confirm-veil" data-testid="menu-abandon-confirm" @click.self="sfx.deselect(); confirmAbandon = false">
      <!-- v5.65 (per Javi): plain card — the parchment lives elsewhere. -->
      <div class="card menu-confirm-card">
        <h2 class="menu-confirm-title"><img src="/ui/icon-warning.webp" alt="⚠️" class="menu-confirm-icon"> {{ t('title.abandonTitle') }}</h2>
        <p class="menu-confirm-body">{{ t('gamemenu.abandonBody') }}</p>
        <div class="menu-confirm-actions">
          <button class="btn btn-danger" data-testid="menu-abandon-yes" @click="onAbandonYes">
            {{ t('menu.abandonRun') }}
          </button>
          <button class="btn btn-frame-white" data-testid="menu-abandon-no" @click="sfx.deselect(); confirmAbandon = false">
            {{ t('title.abandonNo') }}
          </button>
        </div>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<style scoped>
.menu-chip {
  position: relative;
  display: flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(31, 58, 95, 0.15);
  font-size: 0.9rem;
  line-height: 1.35;
  cursor: pointer;
}
/* 🎯 Comfortable touch target (v2.22 convention): invisible padded hit
 * area, same visual chip. */
.menu-chip::before {
  content: '';
  position: absolute;
  inset: -9px -3px;
}
.menu-backdrop {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(21, 41, 66, 0.55);
  backdrop-filter: blur(2px);
}
.game-menu-card {
  display: flex;
  flex-direction: column;
  /* The framed rows carry their own air (plinth + drop shadow). */
  gap: 4px;
  width: min(92vw, 360px);
  max-height: calc(100vh - 32px - var(--safe-y));
  max-height: calc(100svh - 32px - var(--safe-y));
  overflow-y: auto;
  text-align: center;
}
/* ✕ v7.79: on a mouse, a styled WebKit scrollbar draws no arrow buttons (Chrome's thin one keeps them) and its track starts below the inset close; phones keep overlay bars, Firefox its own. */
@media (hover: hover) and (pointer: fine) {
  .game-menu-card::-webkit-scrollbar { width: 8px; }
  .game-menu-card::-webkit-scrollbar-thumb { background: rgba(31, 58, 95, 0.3); border-radius: 4px; }
  .game-menu-card::-webkit-scrollbar-track { background: transparent; margin-top: 22px; }
}
.game-menu-title {
  margin: 0 0 2px;
  font-size: 1.2rem;
}
/* 🖼️ The PROVEN framed-button recipe (index.vue .menu-buttons, per the
 * v1.71/v1.76 lessons): pin the art's TRUE aspect (631×216) so nothing
 * stretches, center the label on the COLORED BOX (its middle sits at
 * 36.5% of the frame — the bottom padding carries the plinth), and pin
 * the icon to the face's left. Raw .btn bars flattened here (Javi:
 * "buttons in the menu are cooked"). */
.game-menu-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: min(280px, 100%);
  aspect-ratio: 631 / 216;
  height: auto;
  margin-inline: auto;
  padding: 0 26px 27px;
  border: none;
  border-radius: 0;
  background: url('/ui/btn-med-blue.webp') center / 100% 100% no-repeat;
  box-shadow: none;
  color: #f2fbff;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 700;
  text-shadow: 0 1px 3px rgba(8, 30, 60, 0.85);
  filter: drop-shadow(0 4px 6px rgba(12, 26, 48, 0.45));
  cursor: pointer;
  transition: transform 0.06s ease, filter 0.1s ease;
}
.game-menu-row:hover {
  filter: drop-shadow(0 4px 6px rgba(12, 26, 48, 0.45)) brightness(1.06);
}
.game-menu-row:active {
  transform: translateY(2px);
}
/* ⚙️ v7.42: the slim switches (music, sound, tap to fast forward). */
.game-menu-switches {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  width: min(280px, 100%);
  margin: 0 auto 4px;
}
.game-menu-volume, .menu-wide-switch { grid-column: 1 / -1; }
.game-menu-volume {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 60px;
  padding: 3px 10px 3px 4px;
  border: 1px solid rgba(31, 58, 95, 0.22);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.55);
  text-align: left;
}
.menu-audio-toggle {
  display: grid;
  place-items: center;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 9px;
  background: transparent;
  cursor: pointer;
}
.menu-audio-toggle:hover { background: rgba(255, 255, 255, 0.6); }
.menu-audio-toggle:focus-visible, .menu-volume-control input:focus-visible { outline: 3px solid #2f80ed; outline-offset: 2px; }
.menu-volume-control { flex: 1; min-width: 0; }
.menu-volume-control label { display: block; font-size: 0.8rem; font-weight: 700; line-height: 1.2; }
.menu-volume-control input { display: block; width: 100%; height: 30px; margin: 0; accent-color: #2874ad; cursor: pointer; }
.game-menu-volume.is-muted label { color: var(--color-ink-soft); }
.game-menu-volume.is-muted .menu-switch-icon { opacity: 0.65; }
.game-menu-switch {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 4px 14px;
  border: 1px solid rgba(31, 58, 95, 0.22);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.55);
  color: var(--color-ink);
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.2;
  text-align: left;
  cursor: pointer;
}
.game-menu-switch:hover { background: rgba(255, 255, 255, 0.8); }
.menu-paired-switch { gap: 6px; padding-inline: 8px; }
.game-menu-switch:focus-visible { outline: 3px solid #2f80ed; outline-offset: 2px; }
.game-menu-switch[aria-pressed="false"] { color: var(--color-ink-soft); }
.menu-switch-icon {
  flex: none;
  width: 26px;
  height: 26px;
  object-fit: contain;
}
.menu-switch-icon.is-off { filter: grayscale(1); opacity: 0.55; }
/* 🎭 v7.56: a fourth switch (Story scenes) made the in-run menu 50px taller.
 * On a short screen the framed doors narrow (their art keeps its aspect); on
 * the shortest the switches slim to 38px, still a 44px target (their hit
 * areas meet in the 6px gaps). "Stop guidance" and Abandon Run stay on a
 * 320x568 phone (tests/first-run-guide-ui.mjs), and the 412x660 phone keeps
 * its 44px switch rows (tests/taps-ui.mjs). */
@media (max-height: 700px) {
  .game-menu-row { width: min(230px, 100%); }
}
@media (max-height: 640px) {
  .game-menu-switch { position: relative; min-height: 38px; padding-block: 2px; }
  .game-menu-switch::before { content: ''; position: absolute; inset: -3px 0; }
  /* 🔉 v7.69: the v7.66 volume rows (60px each) pushed "Stop guidance" + Abandon Run off a 320x568
     phone (first-run-guide-ui); on a short screen they shrink like the switches do. */
  .game-menu-volume { min-height: 44px; padding-block: 1px; }
  .menu-volume-control input { height: 22px; }
}
@media (max-height: 600px) {
  .game-menu-row { width: min(200px, 100%); }
}
.menu-row-icon {
  position: absolute;
  left: 7%;
  top: 36.5%;
  transform: translateY(-50%);
  width: 13%;
  height: 50%;
  object-fit: contain;
}
.menu-row-emoji {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
}
.game-menu-note {
  margin: 2px 0 0;
  font-size: 0.75rem;
  color: var(--color-ink-soft);
}
/* 🎓 v7.41: "Stop guidance" / "Guidance off. Undo" - a plain link, a full
 * 44px to tap - but only 24px of the stack (review: a 44px row pushed Abandon
 * Run off a 320x568 screen): the rest of its hit area rides over the gaps. */
.game-menu-guidance {
  position: relative;
  min-height: 24px;
  margin: 0 auto;
  padding: 0 10px;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--color-ink-soft);
  text-decoration: underline;
  cursor: pointer;
}
.game-menu-guidance-off {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  margin: 0;
  font-size: 0.9rem;
  font-weight: 700;
}
.game-menu-guidance::before {
  content: '';
  position: absolute;
  /* (up over the row above's plinth; down only as far as Abandon Run's edge) */
  inset: -12px 0 -8px;
}
.game-menu-guidance-off .game-menu-guidance { margin: 0; }
/* ⚠️ v5.62: the danger row keeps the red bar look, sized to its shelf. */
.game-menu-danger {
  width: min(280px, 100%);
  margin: 4px auto 0;
}
.menu-confirm-veil {
  position: fixed;
  inset: 0;
  z-index: 62;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(12, 26, 48, 0.6);
}
.menu-confirm-card {
  width: min(340px, 100%);
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: center;
}
.menu-confirm-title {
  margin: 0;
  font-size: 1.15rem;
}
.menu-confirm-icon {
  height: 1.2em;
  vertical-align: -0.2em;
}
.menu-confirm-body {
  margin: 0;
  font-size: 0.92rem;
}
.menu-confirm-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 6px;
}
</style>
